import { db } from '@/db/db';
import { getCachedDoc, getExistingDocOnIndexeddb } from '@/db/queries';
import { getDdocDataById } from '@/services/get-ddoc-data-by-id';
import { IDdocIndexerResponse } from '@/services/get-ddocs-by-portal-address';
import { IDdoc, JSONContent } from '@/types';
import { ARCH_VERSION, CORRUPT_CONTENT, TEMP_DOC_ID } from '@/utils/constants';
import { getContractFileData } from '@/utils/contract-utils';
import { decryptOwnerFile, getIPFSAsset } from '@/utils/file-utils';
import { getDdocOnGunById, isNullish } from '@/utils/gun-utils';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';
import { WorkerInstance } from '@/utils/worker-utils/worker-instance';

export const isCorruptContent = (editorData: JSONContent | string) => {
  if (typeof editorData === 'string') {
    return false;
  }
  return (
    editorData?.content?.length === 2 &&
    JSON.stringify(editorData?.content[0]) ===
      JSON.stringify(CORRUPT_CONTENT) &&
    JSON.stringify(editorData?.content[1]) === JSON.stringify(CORRUPT_CONTENT)
  );
};

export const getDocWithRetry = async (
  userNode: any,
  ddocId: string,
  retries: number = 3
) => {
  let attempts = 0;
  let doc = null;

  const isTemp = await isTempDoc(ddocId);
  if (isTemp) return null;
  const localDoc = await getExistingDocOnIndexeddb(ddocId);
  let docOnGun = null;
  while (!docOnGun && attempts < retries) {
    const currentDocOnGun = await getDdocOnGunById(userNode, ddocId);
    const eitherContentOrTitle = !!(
      currentDocOnGun?.content || currentDocOnGun?.title
    );
    if (currentDocOnGun && eitherContentOrTitle && currentDocOnGun.commentKey)
      docOnGun = currentDocOnGun;
    attempts++;
  }
  doc = await getLatestDoc(localDoc, docOnGun);
  return doc;
};

const getLatestDoc = async (localDoc: IDdoc | null, docOnGun: IDdoc | null) => {
  if (!localDoc && !docOnGun) return null;
  if (!localDoc && docOnGun) return docOnGun;
  if (localDoc && docOnGun) {
    if (
      docOnGun.last_updated > localDoc.last_updated &&
      !isCorruptContent(docOnGun.content)
    ) {
      await db.ddocs.update(localDoc.ddocId, {
        content: docOnGun.content,
        last_updated: docOnGun.last_updated,
        title: docOnGun.title,
      });
      return docOnGun;
    } else {
      return localDoc;
    }
  }
  return null;
};

export const isTempDoc = async (ddocId: string) => {
  const tempDoc = localStorage.getItem(TEMP_DOC_ID);
  return tempDoc === ddocId;
};

export const removeTempDoc = () => {
  localStorage.removeItem(TEMP_DOC_ID);
};

export const getDocOnGun = async (ddocId: string) => {
  if (!KeyStoreInstance.isLegacyUser) return null;

  const userNode = KeyStoreInstance.getGunUserNode();
  // is legacy user but was not able to login to gun
  if (!userNode) return null;

  const docOnGun = await getDocWithRetry(userNode, ddocId);

  return docOnGun;
};

export const compareLatestContent = (
  _ddoc: IDdoc | null,
  contentFromIndexer: string | JSONContent,
  lastTransactionBlockTimestamp: number | undefined
): string | JSONContent => {
  if (!_ddoc && isNullish(lastTransactionBlockTimestamp)) return '';
  if (!_ddoc || isNullish(lastTransactionBlockTimestamp))
    return _ddoc?.content || contentFromIndexer;
  return lastTransactionBlockTimestamp! > _ddoc?.last_updated
    ? contentFromIndexer
    : _ddoc?.content;
};

export const getOwnerLockFromMetadata = async (
  metadata: IDdocIndexerResponse
) => {
  if (metadata.resolvedMetadata.ownerLock)
    return metadata.resolvedMetadata.ownerLock;

  const metadataResponse = await WorkerInstance.getIPFSAssetSequential(
    metadata.metadataIPFSHash
  );
  const ipfsData = metadataResponse?.data;
  if (!ipfsData) throw new Error('Failed to get doc metadata');
  return ipfsData.ownerLock;
};

export const getFileContentHashAndMetadataViaWorker = async ({
  fileId,
  ddocId,
  portalAddressFromUserCache,
}: {
  fileId: number | null | undefined;
  ddocId: string;
  portalAddressFromUserCache: string | undefined;
}) => {
  if (isNullish(fileId)) {
    return { metadata: null, contentIPFSHash: '' };
  }
  try {
    if (!KeyStoreInstance.getPortalAddress()) {
      throw new Error('No portal address');
    }
    const { metadataIPFSHash, contentIPFSHash } = await getContractFileData(
      KeyStoreInstance.getPortalAddress(),
      fileId!
    );
    const metadataResponse =
      await WorkerInstance.getIPFSAssetSequential(metadataIPFSHash);
    const metadata = metadataResponse?.data;

    if (metadata.ddocId !== ddocId || metadata.isDeleted) {
      throw new Error('invalid metadata');
    }

    return { metadata, contentIPFSHash };
  } catch (error) {
    if (isNullish(portalAddressFromUserCache) || isNullish(fileId)) {
      return { metadata: null, contentIPFSHash: '' };
    }
    const { metadataIPFSHash, contentIPFSHash } = await getContractFileData(
      portalAddressFromUserCache!,
      fileId!
    );
    const response = await getIPFSAsset({ ipfsHash: metadataIPFSHash });

    const metadata = response?.data;
    if (metadata.ddocId !== ddocId) {
      throw new Error('invalid metadata');
    }

    return { metadata, contentIPFSHash };
  }
};

export const getDecryptedOwnerLock = async (lock: string) => {
  try {
    return await KeyStoreInstance.decryptData(lock, 'owner');
  } catch (error) {
    return '';
  }
};

export const getDdocContentFromIndexer = async (
  ddocId: string,
  portalAddress: string
) => {
  const ddocDataFromIndexer = await getDdocDataById(ddocId, portalAddress);

  if (!ddocDataFromIndexer) return;

  const encryptedFileKey = (await getOwnerLockFromMetadata(ddocDataFromIndexer))
    .lockedFileKey;

  const decryptedFileKeyContent = await KeyStoreInstance.decryptData(
    encryptedFileKey,
    'owner'
  );

  const decodedFileKey = JSON.parse(
    new TextDecoder().decode(decryptedFileKeyContent)
  ) as { key: string; iv: string; authTag: string };

  // decrypt file content
  const result = await decryptOwnerFile(
    {
      key: decodedFileKey.key,
      iv: decodedFileKey.iv,
      authTag: decodedFileKey.authTag,
    },
    ddocDataFromIndexer.contentIPFSHash
  );

  return { contentFromIndexer: result.file, ddocDataFromIndexer };
};

export const getOwnerDdocContent = async (
  ownerLockedFileKey: string,
  contentHash: string
) => {
  const decryptedFileKeyContent = await KeyStoreInstance.decryptData(
    ownerLockedFileKey,
    'owner'
  );

  const decodedFileKey = JSON.parse(
    new TextDecoder().decode(decryptedFileKeyContent)
  ) as { key: string; iv: string; authTag: string };

  // decrypt file content
  const result = await decryptOwnerFile(
    {
      key: decodedFileKey.key,
      iv: decodedFileKey.iv,
      authTag: decodedFileKey.authTag,
    },
    contentHash
  );

  return result.file;
};

export const isLatestArchVersion = async (
  onChainArchVersion: string,
  walletAddress: string,
  ddocId: string
) => {
  if (onChainArchVersion === ARCH_VERSION) return true;

  // doc can have been published but indexer might not have updated the metadata yet
  // check user cache for the latest arch version also

  const cachedDoc = await getCachedDoc(walletAddress, ddocId);

  return cachedDoc?.archVersion === ARCH_VERSION;
};
